/**
 * Leitura da situação vinda de outro sistema.
 *
 * eSUS e SISREG têm vocabulários próprios ("Agendamento Confirmado" contra
 * "AGENDAMENTO / CONFIRMADO / EXECUTANTE"), e nenhum dos dois é o status do
 * KAVI. A cor sai de palavras-chave, e o que não casa fica neutro — melhor
 * sem cor do que com a cor errada.
 */

import type { ItemHistorico } from './tipos';

export type TomSituacao = 'neutro' | 'sucesso' | 'alerta' | 'perigo';

const REGRAS: [RegExp, TomSituacao][] = [
  [/NEGAD|DEVOLVID|CANCELAD|FALTA|RECUSAD|EXPIRAD/, 'perigo'],
  [/PENDEN|ESPERA|REENVIAD|REGULACAO|ANALISE|SOLICITAD/, 'alerta'],
  [/CONFIRMAD|AGENDAD|AUTORIZAD|ATENDID|EXECUTAD|APROVAD|MARCAD/, 'sucesso'],
];

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

export function tomDaSituacao(situacao: string | null): TomSituacao {
  if (!situacao) return 'neutro';
  const texto = normalizar(situacao);
  return REGRAS.find(([padrao]) => padrao.test(texto))?.[1] ?? 'neutro';
}

/** `AGENDAMENTO / CONFIRMADO / EXECUTANTE` → `Agendamento · confirmado · executante`. */
export function rotuloDaSituacao(situacao: string | null): string {
  if (!situacao) return '—';
  const partes = situacao
    .split('/')
    .map((parte) => parte.trim().toLowerCase())
    .filter(Boolean);
  const texto = partes.join(' · ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** `AAAA-MM-DD` de hoje, no relógio de quem está na tela. */
export function hojeLocal(agora = new Date()): string {
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return `${agora.getFullYear()}-${doisDigitos(agora.getMonth() + 1)}-${doisDigitos(agora.getDate())}`;
}

/**
 * Se a marcação do eSUS pode ser impressa pelo histórico: situação "Agendada"
 * e atendimento de hoje em diante. Histórico passado, cancelado ou já
 * atendido não tem o que levar ao paciente. O backend repete a regra.
 */
export function marcacaoImprimivel(item: ItemHistorico, hoje = hojeLocal()): boolean {
  if (!item.codigo || !item.dataMarcacao) return false;
  return normalizar(item.situacao ?? '').trim() === 'AGENDADA' && item.dataMarcacao >= hoje;
}
